import { creerClient, type Client } from '../../api/client.js';
import { modeMocks } from '../../app/entree.js';
import { creerFetchMocke, sessionDepuisAdresse, sessionPourRole } from '../../mocks/fetch-mocke.js';
import { creerJournal, entiteSynchronisee, type Journal, type StockageLocal, type TransportPush } from './dependances.js';

/**
 * Services des écrans de Brigitte : client de l'API, journal de mutations de l'appareil et transport `sync.push`.
 * Par défaut ils s'appuient sur `fetch` et le stockage du navigateur ; la coquille (ou un test) peut les remplacer par
 * `definirServices` (RFC-L52-02 : la coquille fournira le client déjà configuré, mocks compris).
 */
export interface ServicesBrigitte {
  client: Client;
  journal: Journal;
  differe: Journal;
  estSynchronisee: (entityType: string) => boolean;
  transport: TransportPush;
  deviceId: string;
  horloge: () => string;
  genererId: () => string;
}

/** Instant ISO 8601 au décalage de la Guadeloupe (UTC-4, sans heure d'été). */
export function instantGuadeloupe(date: Date): string {
  return new Date(date.getTime() - 4 * 3_600_000).toISOString().replace('Z', '-04:00');
}

/** UUID v7 (horodaté, ordonné) : identifiant d'une mutation saisie sur l'appareil. */
export function genererUuidV7(maintenant: number = Date.now(), alea: () => number = Math.random): string {
  const octets = Array.from({ length: 16 }, () => Math.floor(alea() * 256));
  const t = BigInt(maintenant);
  for (let i = 0; i < 6; i += 1) octets[i] = Number((t >> BigInt(8 * (5 - i))) & 0xffn);
  octets[6] = (octets[6]! & 0x0f) | 0x70;
  octets[8] = (octets[8]! & 0x3f) | 0x80;
  const hex = octets.map((o) => o.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** Stockage de l'appareil : `localStorage` quand il est disponible, la mémoire sinon (navigation privée, tests). */
export function creerStockageNavigateur(prefixe = 'lakou:brigitte:'): StockageLocal {
  const memoire = new Map<string, string>();
  const stockage = (): Storage | null => {
    try {
      return globalThis.localStorage ?? null;
    } catch {
      return null;
    }
  };
  const lireBrut = (cle: string): string | null => {
    try {
      return stockage()?.getItem(prefixe + cle) ?? memoire.get(cle) ?? null;
    } catch {
      return memoire.get(cle) ?? null;
    }
  };
  const ecrireBrut = (cle: string, valeur: string): void => {
    memoire.set(cle, valeur);
    try {
      stockage()?.setItem(prefixe + cle, valeur);
    } catch {
      // Quota plein ou stockage bloqué : la mémoire garde la saisie pour la session en cours.
    }
  };
  const toutesLesCles = (): string[] => {
    const cles = new Set(memoire.keys());
    try {
      const s = stockage();
      for (let i = 0; s && i < s.length; i += 1) {
        const k = s.key(i);
        if (k?.startsWith(prefixe)) cles.add(k.slice(prefixe.length));
      }
    } catch {
      // Lecture impossible : on se contente de la mémoire.
    }
    return [...cles];
  };
  return {
    async lire<T>(cle: string) {
      const brut = lireBrut(cle);
      return brut === null ? undefined : (JSON.parse(brut) as T);
    },
    async ecrire(cle, valeur) {
      ecrireBrut(cle, JSON.stringify(valeur));
    },
    async supprimer(cle) {
      memoire.delete(cle);
      try {
        stockage()?.removeItem(prefixe + cle);
      } catch {
        // Rien à retirer si le stockage est bloqué.
      }
    },
    async cles(prefixeCle = '') {
      return toutesLesCles()
        .filter((c) => c.startsWith(prefixeCle))
        .sort();
    },
    async vider() {
      for (const c of toutesLesCles()) await this.supprimer(c);
    },
  };
}

/**
 * Mode démonstration (`modeMocks`, comme la coquille) : le client lit les exemples du contrat avec la même session
 * fictive que la coquille, et le transport `sync.push` accuse réception localement. Aucune requête ne quitte la page.
 */
export function creerServicesParDefaut(): ServicesBrigitte {
  const mocks = modeMocks(import.meta.env);
  const client = creerClient({
    fetch: mocks
      ? creerFetchMocke({ session: sessionDepuisAdresse(globalThis.location?.search ?? '') ?? sessionPourRole('assistante') })
      : (...args) => globalThis.fetch(...args),
  });
  const stockage = creerStockageNavigateur();
  const genererId = () => genererUuidV7();
  const horloge = () => instantGuadeloupe(new Date());
  return {
    client,
    journal: creerJournal({ stockage, horloge, genererId }),
    differe: creerJournal({ stockage: creerStockageNavigateur('lakou:brigitte-differe:'), horloge, genererId }),
    estSynchronisee: entiteSynchronisee,
    transport: mocks
      ? async (corps) => ({
          results: corps.mutations.map((m) => ({ mutationId: m.mutationId, result: 'applique' as const, conflict: null })),
        })
      : (corps) => client.appeler('sync.push', { corps }),
    deviceId: `appareil-${genererId()}`,
    horloge,
    genererId,
  };
}

let courants: ServicesBrigitte | null = null;

export function obtenirServices(): ServicesBrigitte {
  courants ??= creerServicesParDefaut();
  return courants;
}

/** Remplace les services (coquille, tests). `null` revient aux services par défaut. */
export function definirServices(services: ServicesBrigitte | null): void {
  courants = services;
}
